<script setup lang="ts">
import useMainNavigation from '~/data/mainNavigation'

const authStore = useAuthStore()
const garageStore = useGarageStore()
const mainStore = useMainStore()
const { logout } = useAuthenticationService()

const { t } = useI18n()

const userId = computed(() => authStore.currentUser?.id)

const mainNavigation = userId.value
  ? useMainNavigation(userId.value.toString(), garageStore, authStore)
  : ref([])
</script>

<template>
  <NavigationTopBarItemBase mouse-over data-testid="top-bar-user-item">
    <template #button>
      <div class="tw-hidden tw-items-center tw-gap-2 md:tw-inline-flex">
        <div class="tw-relative">
          <UserpicItem class="tw-h-9 tw-w-9" />

          <span
            class="tw-absolute tw-right-0 tw-top-0 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-riser-red tw-ring-2 tw-ring-white"
          />
        </div>

        <FontAwesomeIcon icon="chevron-down" class="tw-text-sm" />
      </div>

      <div class="tw-relative md:tw-hidden">
        <FontAwesomeIcon icon="bars" />

        <span
          class="tw-absolute -tw-right-1.5 -tw-top-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-riser-red"
        />
      </div>
    </template>

    <template #default="{ triggerClose }">
      <div class="tw-flex tw-flex-col md:tw-w-max">
        <div class="md:tw-hidden">
          <NuxtLink
            v-for="navItem in mainNavigation"
            :key="navItem.translationKey"
            :to="navItem.route"
            class="menu-item"
            @click="triggerClose"
          >
            <FontAwesomeIcon :icon="navItem.fontAwesomeIcon" class="icon" />

            <div class="tw-ml-3 tw-text-sm">{{ t(navItem.translationKey) }}</div>
          </NuxtLink>
        </div>

        <div class="tw-mt-6 md:tw-mt-0">
          <NuxtLink
            :to="`/user/${authStore.currentUser?.id}`"
            class="menu-item tw-hidden md:tw-flex"
            @click="triggerClose"
          >
            <FontAwesomeIcon icon="user" class="icon" />

            <div class="tw-ml-3 tw-text-sm">{{ t('My Profile') }}</div>
          </NuxtLink>

          <NuxtLink to="/settings" class="menu-item" @click="triggerClose">
            <FontAwesomeIcon icon="gear" class="icon" />

            <div class="tw-ml-3 tw-text-sm">{{ t('Settings') }}</div>
          </NuxtLink>

          <InputAppButton
            class="menu-item tw-w-full"
            data-testid="invite-friend-menu-item"
            @click="
              () => {
                triggerClose()
                mainStore.showInviteFriendModal()
              }
            "
          >
            <FontAwesomeIcon icon="paper-plane" class="icon" />

            <div class="tw-ml-3 tw-text-sm">{{ t('referral_invite_title') }}</div>

            <span class="tw-ml-2 tw-h-2 tw-w-2 tw-rounded-full tw-bg-riser-red" />
          </InputAppButton>

          <a
            href="https://cardoride.com/support"
            target="_blank"
            class="menu-item"
            @click="triggerClose"
          >
            <FontAwesomeIcon icon="envelope" class="icon" />

            <div class="tw-ml-3 tw-text-sm">{{ t('Support') }}</div>
          </a>

          <InputAppButton
            class="menu-item tw-w-full"
            @click="
              () => {
                triggerClose
                logout()
              }
            "
          >
            <FontAwesomeIcon icon="sign-out-alt" class="icon" />

            <div class="tw-ml-3 tw-text-sm">{{ t('Log out') }}</div>
          </InputAppButton>

          <NuxtLink
            v-if="!authStore.isUserPro"
            to="/upgrade"
            class="menu-item tw-gap-3"
            @click="triggerClose"
          >
            <IconsProBadge class="tw-w-3" />

            {{ t('Upgrade-(pro)') }}
          </NuxtLink>
        </div>
      </div>
    </template>
  </NavigationTopBarItemBase>
</template>

<style scoped>
.menu-item {
  @apply tw-flex tw-items-center tw-border-b tw-border-black/10 tw-bg-white tw-px-8 tw-py-2 tw-text-sm tw-transition-all tw-duration-200 last:tw-border-b-0 hover:tw-bg-gray-100;
}

.menu-item .icon {
  @apply tw-h-3 tw-w-3 tw-text-gray-800;
}
</style>
